import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { from, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { isTokenValid } from '@lib/helpers/general.helper';
import * as microsoftTeams from '@microsoft/teams-js';
import { isOfficeEnvironment } from '../utils/office_environment.util';

@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  private oAuthService = inject(OAuthService);
  private readonly grantType = 'office_add_in';

  intercept(request: HttpRequest<any>, next: HttpHandler) {
    if (request.url.endsWith('json')) {
      return next.handle(request);
    }
    const isValidToken = isTokenValid(this.oAuthService.getAccessToken());
    if (isValidToken) {
      const newRequest = request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.oAuthService.getAccessToken()}`,
        },
      });
      return next.handle(newRequest);
    }
    return next.handle(request).pipe(
      catchError((error) => {
        if (error.status === 401) {
          const refreshPromise = isOfficeEnvironment() ? this.refreshWithTeamsSSO() : this.oAuthService.silentRefresh();

          return from(refreshPromise).pipe(
            switchMap(() => {
              const newRequest = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${this.oAuthService.getAccessToken()}`,
                },
              });
              return next.handle(newRequest);
            }),
          );
        } else {
          return throwError(() => error);
        }
      }),
    );
  }

  private async refreshWithTeamsSSO(): Promise<void> {
    const ssoToken = await microsoftTeams.authentication.getAuthToken();
    await this.oAuthService.fetchTokenUsingGrant(this.grantType, { ssoToken });
  }
}
